import { familyTagsOf } from '../../../../shared/utils/family-tags.util';
import { formatActiveSubscriptionLastWashedDate } from '../../../way-purchase-modal/utils/way-purchase-display.helpers';
import {
  coerceUsStateCode,
  splitLicensePlateForApi,
} from '../../../../shared/utils/split-license-plate-for-api';
import type { SubscriptionPause } from '../../../../shared/types/subscription-pause.types';
import { readSubscriptionPause } from '../../../../shared/utils/subscription-pause.util';
import { isReactivatedPausedRead } from '../../../../shared/utils/paused-subscription-notification.util';
import { toVendorUsaPhoneInputValue } from './vendor-active-add-payment.util';
import {
  buildActiveMembershipStatusStrip,
  type MembershipStatusStripView,
} from '../../utils/membership-status-strip.util';

export interface VendorActiveSubscriptionDisplay {
  customerName: string;
  licensePlate: string;
  packageName: string;
  /** CWS-1582 §8: part of a family plan / its primary; drives the Family plan and Primary pills. */
  isFamily: boolean;
  isPrimary: boolean;
  packageId: string | null;
  totalWashesAvailable: string;
  totalWashesRedeemed: string;
  lastWashedDate: string;
  statusLabel: string;
  sourceLabel: string;
  /** Linked membership order UUID when created at redeem. */
  orderId: string | null;
  paymentStatus: string | null;
  /** True when attendant should see Collect payment UI (optional until expiry). */
  paymentRequired: boolean;
  /** True after Way card-on-file is saved for this imported membership. */
  cardDetailsCollected: boolean;
  /** Amount due today (0 for imported membership card-on-file). */
  finalPrice: number | null;
  /** Recurring monthly amount (charged starting at billingStartsAt / expiry). */
  listPrice: number | null;
  membershipStartDate: string;
  /** ISO yyyy-MM-dd — earliest selectable membership start (= expiry / auto-renewal). */
  membershipStartMinDate: string;
  renewalDateLabel: string;
  phoneNumber: string;
  /** `subscriptions.id` when the payload carries it (LPR event or lookup); drives Update Membership (CWS-1426). */
  subscriptionId: string | null;
  /** Raw `subscriptions.source`; only `way` memberships can change plan here. */
  sourceRaw: string | null;
  /** Owner of the membership, to read its renewal date for the pause modal (CWS-1547). */
  customerId?: string | null;
  /** Pause block (CWS-1544); null/omitted when pausing is switched off. */
  pause?: SubscriptionPause | null;
  /** A paused read reactivated from this popup (CWS-1469): shows "Successfully Reactivated". */
  reactivated?: boolean;
  /** CWS-1730: the DRB member's status line (`membership_status`); null when there is none. */
  statusStrip?: MembershipStatusStripView | null;
  /**
   * CWS-1780: the membership is cancelling at its period end and still redeems until then
   * (`subscription_cancellation` on the lane payload). Null when it is not cancelling.
   */
  cancellation?: MembershipCancellationNotice | null;
}

export interface MembershipCancellationNotice {
  /** ISO instant the paid period ends; null when the payload has no date. */
  endsAt: string | null;
}

const LANE_SOURCE_VALUES = new Set(['lpr', 'shop', 'order', 'order_request_lookup', 'way']);

/** True when the value is a third-party vendor tag (drb, everwash, …), not a lane/Way source. */
export function isVendorMembershipSource(value: string | null | undefined): boolean {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase();
  return Boolean(normalized) && normalized !== 'way' && !LANE_SOURCE_VALUES.has(normalized);
}

function pickString(...values: any[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return '';
}

function resolveLicensePlate(data: Record<string, any>): string {
  const raw = pickString(data['vehicle_license_plate'], data['license_plate'], data['plate']);
  if (!raw) {
    return '—';
  }
  const hintState = coerceUsStateCode(pickString(data['state'], data['vehicle_state']) || null);
  const { license_plate } = splitLicensePlateForApi(raw, hintState);
  return (license_plate || raw).trim().toUpperCase();
}

/** Customer display name for Active Subscription (LPR / vendor / list reopen). */
export function resolveActiveSubscriptionCustomerName(
  data: Record<string, any> | null | undefined
): string {
  if (!data || typeof data !== 'object') {
    return '—';
  }
  const nested =
    data['data'] && typeof data['data'] === 'object' && !Array.isArray(data['data'])
      ? (data['data'] as Record<string, any>)
      : null;
  const sources = [data, nested].filter(Boolean) as Record<string, any>[];

  for (const row of sources) {
    const first = pickString(
      row['customer_first_name'],
      row['customerFirstName'],
      row['first_name']
    );
    const last = pickString(row['customer_last_name'], row['customerLastName'], row['last_name']);
    const combined = [first, last].filter(Boolean).join(' ').trim();
    if (combined) {
      return combined;
    }

    const customer = row['customer'];
    if (customer && typeof customer === 'object' && !Array.isArray(customer)) {
      const nestedCustomer = customer as Record<string, any>;
      const nestedFirst = pickString(
        nestedCustomer['first_name'],
        nestedCustomer['customer_first_name']
      );
      const nestedLast = pickString(
        nestedCustomer['last_name'],
        nestedCustomer['customer_last_name']
      );
      const nestedCombined = [nestedFirst, nestedLast].filter(Boolean).join(' ').trim();
      if (nestedCombined) {
        return nestedCombined;
      }
      const nestedName = pickString(nestedCustomer['name'], nestedCustomer['customer_name']);
      if (nestedName) {
        return nestedName;
      }
    }

    const full = pickString(row['customer_name'], row['customerName'], row['name']);
    if (full) {
      return full;
    }
  }

  return '—';
}

function resolveCustomerName(data: Record<string, any>): string {
  return resolveActiveSubscriptionCustomerName(data);
}

function resolveSourceLabel(data: Record<string, any>): string {
  const customerSource = pickString(data['customer_source'], data['customers_source']);
  if (isVendorMembershipSource(customerSource)) {
    return customerSource.toUpperCase();
  }

  const raw = pickString(data['source']);
  if (isVendorMembershipSource(raw)) {
    return raw.toUpperCase();
  }

  // Lane events often set `source: 'lpr'`. That is not a vendor tag — do not
  // label in-shop / Way memberships as DRB just because vendor mode is on.
  return 'In-shop';
}

function resolveWashesAvailable(data: Record<string, any>): string {
  const value = data['total_washes_available'];
  if (value == null || value === '') {
    return 'Unlimited';
  }
  return String(value);
}

function resolveWashesRedeemed(data: Record<string, any>): string {
  const value = data['total_washes_redeemed'];
  if (value == null || value === '') {
    return '0';
  }
  return String(value);
}

function toIsoDateInputValue(value: any): string {
  if (value == null || value === '') {
    return '';
  }
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatExpiryLabel(value: any): string {
  if (value == null || value === '') {
    return '';
  }
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function resolvePhoneNumber(data: Record<string, any>): string {
  const nested = data['customer'];
  let raw = '';
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    raw = pickString((nested as Record<string, any>)['contact']);
  }
  if (!raw) {
    raw = pickString(
      data['customer_contact'],
      data['contact'],
      data['phone'],
      data['phone_number']
    );
  }
  return toVendorUsaPhoneInputValue(raw);
}

function resolvePaymentRequired(data: Record<string, any>): boolean {
  if (typeof data['payment_required'] === 'boolean') {
    return data['payment_required'];
  }
  if (typeof data['has_payment'] === 'boolean') {
    return !data['has_payment'];
  }
  const status = pickString(data['payment_status']).toLowerCase();
  if (status === 'pending') {
    return true;
  }
  // Vendor-sourced active membership with no Way Stripe billing yet.
  const source = pickString(data['customer_source'], data['source']).toLowerCase();
  return isVendorMembershipSource(source);
}

function resolveCardDetailsCollected(data: Record<string, any>, paymentRequired: boolean): boolean {
  if (data['card_details_collected'] === true || data['cardDetailsCollected'] === true) {
    return true;
  }
  if (paymentRequired) {
    return false;
  }
  const source = pickString(data['customer_source'], data['source']);
  if (!isVendorMembershipSource(source)) {
    return false;
  }
  if (typeof data['has_payment'] === 'boolean') {
    return data['has_payment'];
  }
  if (typeof data['payment_required'] === 'boolean') {
    return !data['payment_required'];
  }
  return false;
}

function resolveMoneyField(data: Record<string, any>, key: string): number | null {
  const raw = data[key];
  if (raw == null || raw === '') {
    return null;
  }
  const amount = Number(raw);
  return Number.isFinite(amount) ? amount : null;
}

function resolveFinalPrice(data: Record<string, any>): number | null {
  const dueToday =
    resolveMoneyField(data, 'amount_due_today') ?? resolveMoneyField(data, 'final_price');
  if (dueToday != null) {
    return dueToday;
  }
  // Fallback for older payloads that only sent list_price.
  return resolveMoneyField(data, 'list_price');
}

function firstPositiveMoney(data: Record<string, any>, ...keys: string[]): number | null {
  for (const key of keys) {
    const amount = resolveMoneyField(data, key);
    if (amount != null && amount > 0) {
      return amount;
    }
  }
  return null;
}

function resolveListPrice(data: Record<string, any>): number | null {
  return (
    firstPositiveMoney(data, 'recurring_monthly_price', 'list_price') ??
    resolveMoneyField(data, 'amount_due_today') ??
    resolveMoneyField(data, 'final_price')
  );
}

function resolveOrderId(data: Record<string, any>): string | null {
  const value = pickString(data['order_id']);
  return value || null;
}

function resolveMembershipStartDate(data: Record<string, any>): string {
  const nested = data['subscription'];
  const nestedEnd =
    nested && typeof nested === 'object' && !Array.isArray(nested)
      ? (nested as Record<string, any>)['current_period_end']
      : undefined;
  const nestedStart =
    nested && typeof nested === 'object' && !Array.isArray(nested)
      ? (nested as Record<string, any>)['current_period_start']
      : undefined;
  return (
    toIsoDateInputValue(data['membership_start_date']) ||
    toIsoDateInputValue(data['billing_starts_at']) ||
    toIsoDateInputValue(data['current_period_end']) ||
    toIsoDateInputValue(nestedEnd) ||
    toIsoDateInputValue(data['current_period_start']) ||
    toIsoDateInputValue(nestedStart)
  );
}

function resolveExpiryDateLabel(data: Record<string, any>): string {
  const nested = data['subscription'];
  const nestedEnd =
    nested && typeof nested === 'object' && !Array.isArray(nested)
      ? (nested as Record<string, any>)['current_period_end']
      : undefined;
  return (
    formatExpiryLabel(data['membership_start_date']) ||
    formatExpiryLabel(data['billing_starts_at']) ||
    formatExpiryLabel(data['current_period_end']) ||
    formatExpiryLabel(nestedEnd)
  );
}

export function buildVendorActiveSubscriptionDisplayVm(
  data: Record<string, any> | null | undefined
): VendorActiveSubscriptionDisplay {
  const row = data && typeof data === 'object' ? data : {};
  const sourceLabel = resolveSourceLabel(row);
  const expiryDateLabel = resolveExpiryDateLabel(row);
  const membershipStartDate = resolveMembershipStartDate(row);
  const paymentRequired = resolvePaymentRequired(row);

  return {
    customerName: resolveCustomerName(row),
    licensePlate: resolveLicensePlate(row),
    packageName: pickString(row['package_name']) || '—',
    ...familyTagsOf(row),
    packageId: pickString(row['package_id']) || null,
    totalWashesAvailable: resolveWashesAvailable(row),
    totalWashesRedeemed: resolveWashesRedeemed(row),
    lastWashedDate: formatActiveSubscriptionLastWashedDate(
      pickString(row['last_washed_date']) || null
    ),
    statusLabel: 'Subscription Active',
    sourceLabel,
    orderId: resolveOrderId(row),
    paymentStatus: pickString(row['payment_status']) || null,
    paymentRequired,
    cardDetailsCollected: resolveCardDetailsCollected(row, paymentRequired),
    finalPrice: resolveFinalPrice(row),
    listPrice: resolveListPrice(row),
    membershipStartDate,
    membershipStartMinDate: membershipStartDate,
    renewalDateLabel: expiryDateLabel,
    phoneNumber: resolvePhoneNumber(row),
    subscriptionId: resolveSubscriptionId(row),
    sourceRaw: resolveSourceRaw(row),
    customerId: pickString(row['customer_id']) || null,
    pause: readSubscriptionPause(row['pause']),
    reactivated: isReactivatedPausedRead(row),
    cancellation: readMembershipCancellation(row['subscription_cancellation']),
    statusStrip: buildActiveMembershipStatusStrip(row['membership_status']),
  };
}

/** The `subscription_cancellation` block, or null when absent or not a period-end cancel. */
export function readMembershipCancellation(raw: unknown): MembershipCancellationNotice | null {
  const block =
    raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null;
  if (!block || block['at_period_end'] !== true) return null;
  const endsAt = pickString(block['ends_at']);
  return { endsAt: endsAt || null };
}

function resolveSubscriptionId(row: Record<string, any>): string | null {
  const direct = pickString(row['subscription_id']);
  if (direct) return direct;
  const nested = row['subscription'];
  return nested && typeof nested === 'object' ? pickString(nested['id']) || null : null;
}

function resolveSourceRaw(row: Record<string, any>): string | null {
  const nested = row['subscription'];
  const candidate =
    pickString(row['subscription_source']) ||
    (nested && typeof nested === 'object' ? pickString(nested['source']) : '') ||
    pickString(row['source']) ||
    pickString(row['customer_source']);
  return candidate ? candidate.trim().toLowerCase() : null;
}

export function buildVendorMembershipAlertMessage(
  sourceLabel: string,
  expiryDateLabel: string,
  cardDetailsCollected = false
): string {
  const expiry = expiryDateLabel ? ` Expiry: ${expiryDateLabel}` : '';
  if (cardDetailsCollected) {
    return `Card details collected.${expiry}`;
  }
  const source = sourceLabel && sourceLabel !== 'In-shop' ? sourceLabel : 'DRB';
  return `${source} Membership Alert! Collect payment to add membership in the system.${expiry}`;
}
